import type { NotificationDetailAction } from "./notificationDetailActions"
import { buildProfileBrokerApiUrl } from "./profileBrokerNavigation"

function pickStr(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === "string" && v.trim()) return v.trim()
  }
  return undefined
}

const BROKER_SLUG_DISPLAY: Record<string, string> = {
  liquidbrokers: "Liquid Brokers",
  liquid: "Liquid Brokers",
  oanda: "OANDA",
  ibkr: "Interactive Brokers",
  binance: "Binance",
  ctrader: "cTrader",
  custom_csv: "Custom CSV",
  customcsv: "Custom CSV",
}

/** Human label for notification copy (prefers display fields, then known slug map). */
export function formatBrokerNotificationLabel(raw: string): string {
  const t = raw.trim()
  if (!t) return "your broker"
  if (/\s/.test(t) || /[A-Z]/.test(t)) return t
  const slug = t.toLowerCase().replace(/[\s_-]+/g, "")
  if (BROKER_SLUG_DISPLAY[slug]) return BROKER_SLUG_DISPLAY[slug]
  if (BROKER_SLUG_DISPLAY[t.toLowerCase()]) return BROKER_SLUG_DISPLAY[t.toLowerCase()]
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

export function resolveBrokerManagementDeepLink(details: unknown): {
  brokerName?: string
  accountCode?: string
  path: string
} | null {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return null
  }
  const obj = details as Record<string, unknown>
  const brokerName = pickStr(obj, ["broker_name", "brokerName", "broker"])
  const accountCode = pickStr(obj, [
    "broker_account_code",
    "brokerAccountCode",
    "account_code",
    "accountCode",
  ])
  if (!brokerName && !accountCode) return null

  return {
    brokerName,
    accountCode,
    path: buildProfileBrokerApiUrl({
      brokerName,
      accountCode,
      brokerTab: "sync",
    }),
  }
}

/** @deprecated Prefer resolveBrokerManagementDeepLink */
export function resolveBrokerDisconnectedDeepLink(details: unknown): {
  brokerName: string
  accountCode?: string
  reconnectPath: string
} | null {
  const links = resolveBrokerManagementDeepLink(details)
  if (!links?.brokerName) return null
  return {
    brokerName: links.brokerName,
    accountCode: links.accountCode,
    reconnectPath: links.path,
  }
}

export function buildBrokerDisconnectedDetailActions(
  details: unknown
): NotificationDetailAction[] {
  const links = resolveBrokerManagementDeepLink(details)
  if (!links?.brokerName) return []

  return [
    {
      id: "reconnect",
      rowLabel: "Broker",
      buttonLabel: "Reconnect",
      path: links.path,
    },
  ]
}

export function buildBrokerReconnectedDetailActions(
  details: unknown
): NotificationDetailAction[] {
  const links = resolveBrokerManagementDeepLink(details)
  if (!links?.brokerName) return []

  return [
    {
      id: "view-connection",
      rowLabel: "Broker",
      buttonLabel: "View connection",
      path: links.path,
    },
  ]
}

export function buildSyncFailedDetailActions(
  details: unknown
): NotificationDetailAction[] {
  const links = resolveBrokerManagementDeepLink(details)
  if (!links) return []

  return [
    {
      id: "check-sync",
      rowLabel: "Sync",
      buttonLabel: "Check sync",
      path: links.path,
    },
  ]
}

export function buildSyncRecoveredDetailActions(
  details: unknown
): NotificationDetailAction[] {
  const links = resolveBrokerManagementDeepLink(details)
  if (!links) return []

  return [
    {
      id: "view-connection",
      rowLabel: "Sync",
      buttonLabel: "View connection",
      path: links.path,
    },
  ]
}
